import { useHistory } from 'react-router-dom';
import { Panel } from 'reactflow';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { faRedo, faUndo } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { deleteDraftWorkflowVersion } from 'api/workflows';
import { historySelector, useReactFlowStore } from 'assets/js/pages/engage/workflows/utils/store';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentWorkflow } from 'customHooks/useCurrentWorkflow';
import { queryClient } from 'queries/queryClient';
import { workflowQueryKeys } from 'queries/workflow-query-keys';
import { GivebutterAxiosError } from 'types/error';

const UndoRedoPanel = () => {
    const { error, success } = useToast();
    const { accountId } = useCurrentAccount();
    const { data: workflow } = useCurrentWorkflow();
    const history = useHistory();

    const { canUndo, canRedo, undo, redo } = useReactFlowStore(historySelector);

    const { mutate: resetChanges, isPending } = useMutation<unknown, GivebutterAxiosError>({
        mutationFn: async () => {
            await deleteDraftWorkflowVersion(accountId, workflow.id);
        },
        onSuccess: async (_response, _variables) => {
            queryClient.removeQueries({
                queryKey: workflowQueryKeys.workflowById(accountId, workflow.id),
            });

            success('Changes reset!');
            history.push(`/accounts/${accountId}/workflows/${workflow.id}`);
        },
        onError: e => {
            error(e.response?.data.message || 'Unable to reset changes. Please try again');
        },
    });

    return (
        <Panel position="top-left">
            <div className="flex items-center gap-4">
                <div className="flex items-center">
                    <Button
                        onClick={() => undo()}
                        variant="outline"
                        color="neutral"
                        shape={'square'}
                        size="sm"
                        className="!rounded-r-none !border-r-0"
                        disabled={!canUndo}
                        aria-label="Undo"
                    >
                        <FontAwesomeIcon icon={faUndo} />
                    </Button>
                    <Button
                        onClick={() => redo()}
                        variant="outline"
                        color="neutral"
                        shape={'square'}
                        size="sm"
                        className="!rounded-l-none"
                        disabled={!canRedo}
                        aria-label="Redo"
                    >
                        <FontAwesomeIcon icon={faRedo} />
                    </Button>
                </div>
                {workflow.has_draft_version ? (
                    <Button
                        variant="outline"
                        color="neutral"
                        size="sm"
                        className="!w-auto flex-grow"
                        disabled={isPending}
                        onClick={() => resetChanges()}
                    >
                        Reset changes
                    </Button>
                ) : null}
            </div>
        </Panel>
    );
};

export { UndoRedoPanel };
